import { createSlice, current } from '@reduxjs/toolkit';
import { t } from 'i18next';
import roles from '../../app/constants/roles';
import { showSuccess } from '../../app/utils/sweetAlert';
import {
	signIn,
	verifing,
	forgotPassword,
	getUserRoles,
	getTokenInfo,
	updateTokenValidationTime
} from '../apis/authAPIs';
interface AuthState {
	isLoggedIn: boolean;
	loading: boolean;
	directAccess: boolean;
	error: string;
	token: any; // accessToken, idToken, refreshToken and tokenInfo
	userInfo: Object;
	userRoles: Array<any>; // Current loggedin user roles one or more
	needsVerification: boolean; // If user needs to be verified with OTP flow
	// access: boolean;
	deviceToken: string;
	usingMobile: boolean; // If user is logged in using mobile number
	userCurrentlyUsing: string;
	countryCode: string;
	tempOTP: string; // This is temporary OTP needs to send in reset password API when doing reset using mobile number
	sessionExpired: boolean;
	currentRole: Object; // Current loggedin user role
	isSystemUser: boolean;
	tokenValidTimeInfo: Object;
	tokenValidTime: number;
	selectedLanguage: any;
	permissions: any;
}

const initialState: AuthState = {
	isLoggedIn: false,
	directAccess: false,
	loading: false,
	error: '',
	token: {},
	userInfo: {},
	userRoles: [],
	needsVerification: false,
	// access: false,
	deviceToken: '',
	usingMobile: false,
	userCurrentlyUsing: '',
	countryCode: '',
	tempOTP: '',
	permissions: [],
	sessionExpired: false,
	currentRole: {},
	isSystemUser: false,
	tokenValidTimeInfo: {},
	tokenValidTime: 4,
	selectedLanguage: null
};

const authSlice = createSlice({
	name: 'auth',
	initialState,
	reducers: {
		logout(state) {
			state.isLoggedIn = false;
			state.loading = false;
			state.error = '';
			state.token = {};
			state.sessionExpired = false;
			state.userRoles = [];
			state.currentRole = {};
			state.directAccess = false;
			// state.selectedLanguage = null
		},
		setSelectedLanguage(state, action: any) {
			state.selectedLanguage = action.payload;
		},
		loginData(state, action: any) {
			state.loading = action.payload.data;
		},
		saveDeviceToken(state, action: any) {
			state.deviceToken = action.payload;
		},
		setDirectAccess(state, action: any) {
			state.directAccess = action.payload;
		},
		setSessionExpired(state) {
			state.sessionExpired = true;
		},
		usingMobile(state, action: any) {
			state.usingMobile = action.payload;
		},
		currentlyUsing(state, action: any) {
			state.userCurrentlyUsing = action.payload?.value;
			state.countryCode = action.payload.countryCode;
		},
		tempOTP(state, action: any) {
			state.tempOTP = action.payload;
		},
		permissions(state, action: any) {
			state.permissions = action.payload;
		},
		setCurrentUserRole(state, action: any) {
			state.isLoggedIn = true;
			state.currentRole = {
				...action.payload,
				...action.payload.role_id,
				role_id: action.payload.role_id._id,
				_id: action.payload._id
			};
			if (
				action.payload?.role_id?.name === roles.systemUser ||
				action.payload?.role_id?.name === roles.systemOwner
			) {
				state.isSystemUser = true;
			} else {
				state.isSystemUser = false;
			}
		},
		updateCurrentUserRole(state, action) {
			state.currentRole = {
				...action.payload
			};
		},
		updateUserInfo(state, action: any) {
			state.userInfo = action.payload;
		},
		updateAccessToken(state, action: any) {
			state.token.accessToken = action.payload;
		},
		updateTokenTime(state, action: any) {
			state.tokenValidTime = action.payload;
		},
		addUserRoles(state, action) {
			state.userRoles = state.userRoles.concat(action.payload);
		},
		setLoading(state, action) {
			state.loading = action.payload;
		}
	},
	extraReducers: builder => {
		builder.addCase(signIn.pending, (state: AuthState) => {
			state.loading = true;
			state.error = '';
		});
		builder.addCase(signIn.fulfilled, (state: AuthState, action: any) => {
			state.loading = false;
			state.error = '';
			state.token = action.payload?.data?.data?.tokens;
			state.userInfo = action.payload?.data?.data?.userDetails;
			state.userRoles = action.payload?.data?.data?.userRoles;
			state.directAccess = false;
			state.needsVerification = action.payload?.data?.data?.needsVerification;
			if (action.payload?.data?.data?.needsVerification === false) {
				state.isLoggedIn = true;
				const currentRole = action.payload?.data?.data?.userRoles[0];
				state.currentRole = {
					...currentRole,
					...currentRole.role_id,
					role_id: currentRole.role_id._id,
					_id: currentRole._id
				};
				if (
					currentRole?.role_id?.name === roles.systemUser ||
					currentRole?.role_id?.name === roles.systemOwner
				) {
					state.isSystemUser = true;
				} else {
					state.isSystemUser = false;
				}
			}
		});
		builder.addCase(signIn.rejected, (state: AuthState, action: any) => {
			state.loading = false;
			state.error = action.payload;
		});
		builder.addCase(verifing.pending, (state: AuthState) => {
			state.loading = true;
			state.error = '';
		});
		builder.addCase(verifing.fulfilled, (state: AuthState) => {
			// console.log(current(state));
			state.loading = false;
			state.needsVerification = false;
			// state.access = true;
			state.error = '';
			state.isLoggedIn = true;
			const currentRole = current(state.userRoles[0]);
			state.currentRole = {
				...currentRole,
				...currentRole.role_id,
				role_id: currentRole.role_id._id,
				_id: currentRole._id
			};
			if (
				currentRole?.role_id?.name === roles.systemUser ||
				currentRole?.role_id?.name === roles.systemOwner
			) {
				state.isSystemUser = true;
			} else {
				state.isSystemUser = false;
			}
		});
		builder.addCase(verifing.rejected, (state: AuthState) => {
			state.loading = false;
			state.error = 'OTP is invalid';
		});
		builder.addCase(forgotPassword.pending, (state: AuthState) => {
			state.loading = true;
			state.error = '';
		});
		builder.addCase(forgotPassword.fulfilled, (state: AuthState) => {
			state.loading = false;
			state.needsVerification = true;
			// state.access = true;
			state.error = '';
		});
		builder.addCase(forgotPassword.rejected, (state: AuthState) => {
			state.loading = false;
			state.error = 'Something went wrong';
		});
		builder.addCase(getUserRoles.pending, (state: AuthState) => {
			state.loading = true;
			state.error = '';
		});
		builder.addCase(getUserRoles.fulfilled, (state: AuthState, action: any) => {
			state.loading = false;
			state.error = '';
			state.userRoles = action.payload.data;
		});
		builder.addCase(getUserRoles.rejected, (state: AuthState) => {
			state.loading = false;
			state.error = 'Something went wrong';
		});
		builder.addCase(getTokenInfo.pending, (state: AuthState) => {
			state.loading = true;
			state.error = '';
		});
		builder.addCase(getTokenInfo.fulfilled, (state: AuthState, action: any) => {
			state.loading = false;
			state.error = '';
			state.tokenValidTimeInfo = action.payload.data;
			state.tokenValidTime = action.payload?.data?.access_token_info?.time;
		});
		builder.addCase(getTokenInfo.rejected, (state: AuthState) => {
			state.loading = false;
			state.error = 'Something went wrong';
		});
		builder.addCase(
			updateTokenValidationTime.fulfilled,
			(state: AuthState, action: any) => {
				state.loading = false;
				state.error = '';
				showSuccess({ message: t(action.payload.message) });
			}
		);
	}
});

export const {
	logout,
	loginData,
	saveDeviceToken,
	setSessionExpired,
	usingMobile,
	currentlyUsing,
	setDirectAccess,
	tempOTP,
	permissions,
	updateCurrentUserRole,
	setCurrentUserRole,
	updateUserInfo,
	updateAccessToken,
	updateTokenTime,
	setSelectedLanguage,
	addUserRoles,
	setLoading
} = authSlice.actions;

export default authSlice.reducer;
